/**
 * Глобальный менеджер звуков с пулом аудио-объектов
 * Решает проблему с автовоспроизведением и задержками после пауз
 */

class SoundManager {
  private static instance: SoundManager;
  // Пул аудио-объектов: для каждого звука создаем несколько копий
  private soundPools: Map<string, HTMLAudioElement[]> = new Map();
  // Индексы для ротации объектов в пуле
  private poolIndices: Map<string, number> = new Map();
  private initialized: boolean = false;
  private soundEnabled: boolean = true;
  private readonly POOL_SIZE = 5; // Количество копий каждого звука

  // Фоновая музыка
  private backgroundMusic: HTMLAudioElement | null = null;
  private musicEnabled: boolean = true;
  private currentTrack: string = 'lunar_landing';
  private firstInteraction: boolean = true; // Флаг первого взаимодействия

  // Флаги для корректной инициализации аудио
  private audioContextUnlocked: boolean = false;  // Аудио контекст разблокирован
  private pendingMusicPlay: boolean = false;      // Отложенное воспроизведение музыки

  // Web Audio API AudioContext для разблокировки на Android
  private audioContext: AudioContext | null = null;

  // Список доступных треков
  private readonly MUSIC_TRACKS = {
    lunar_landing: {
      file: 'Lunar_Landing_Lounge.mp3',
      name: 'Lunar Landing Lounge'
    },
    lunar_drifter: {
      file: 'Lunar_Drifter.mp3',
      name: 'Lunar Drifter'
    },
    nebula_dreams: {
      file: 'Nebula_Dreams.mp3',
      name: 'Nebula Dreams'
    },
    main_menu: {
      file: 'Main_menu_music_back.mp3',
      name: 'Main Menu Theme'
    }
  };

  private constructor() {}

  static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  /**
   * ANDROID FIX: Создать и разблокировать AudioContext СИНХРОННО в user gesture
   * Это ДОЛЖНО вызываться ПЕРВЫМ в обработчике user gesture!
   * После этого HTMLAudioElement.play() будет работать
   */
  resumeAudioContext(): boolean {
    try {
      // Создаём AudioContext если ещё нет
      if (!this.audioContext) {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioContextClass) {
          this.audioContext = new AudioContextClass();
          console.log('🔊 [Android] AudioContext создан, state:', this.audioContext.state);
        } else {
          console.warn('🔊 [Android] AudioContext не поддерживается');
          return false;
        }
      }

      // Если suspended - вызываем resume() СИНХРОННО
      if (this.audioContext.state === 'suspended') {
        console.log('🔊 [Android] Вызываем AudioContext.resume()...');
        // resume() возвращает Promise, но сам вызов синхронный и это важно!
        this.audioContext.resume().then(() => {
          console.log('🔊 [Android] AudioContext resumed успешно, state:', this.audioContext?.state);
        }).catch(e => {
          console.warn('🔊 [Android] AudioContext resume error:', e);
        });
      }

      // Воспроизводим тихий буфер через AudioContext для полной разблокировки
      if (this.audioContext.state === 'running' || this.audioContext.state === 'suspended') {
        const oscillator = this.audioContext.createOscillator();
        const gainNode = this.audioContext.createGain();
        gainNode.gain.value = 0.001; // Почти беззвучно
        oscillator.connect(gainNode);
        gainNode.connect(this.audioContext.destination);
        oscillator.start(0);
        oscillator.stop(this.audioContext.currentTime + 0.001);
        console.log('🔊 [Android] Silent oscillator played');
      }

      this.audioContextUnlocked = true;
      return true;
    } catch (e) {
      console.warn('🔊 [Android] Ошибка создания AudioContext:', e);
      return false;
    }
  }

  /**
   * Инициализация всех звуков
   * Создает пул аудио-объектов для каждого звука
   */
  init() {
    if (this.initialized) return;

    // Список всех звуков в приложении
    const soundPaths = [
      'ui/ui_tab_switch.mp3',
      'ui/ui_button_click.mp3',
      'ui/ui_modal_open.mp3',
      'ui/claim_click.mp3',
      'ui/claim_success.mp3',
      'ui/promo_celebrate.mp3',
      'ui/marathon_celebrate.mp3',
      'games/Game_start.mp3',
      'games/Winner_game.mp3',
      'games/Loss_game.mp3',
    ];

    // Создаем пул для каждого звука
    soundPaths.forEach(path => {
      const pool: HTMLAudioElement[] = [];

      // Создаем несколько копий каждого звука
      for (let i = 0; i < this.POOL_SIZE; i++) {
        const audio = new Audio(`/assets/sounds/${path}`);
        audio.volume = 0.5; // Громкость 50%
        audio.preload = 'auto';

        // Обработчик окончания воспроизведения
        audio.addEventListener('ended', () => {
          audio.currentTime = 0;
        });

        pool.push(audio);
      }

      this.soundPools.set(path, pool);
      this.poolIndices.set(path, 0);
    });

    this.initialized = true;
    console.log('🔊 SoundManager initialized with', this.soundPools.size, 'sound pools');

    // Для iOS: Регистрируем обработчики первого взаимодействия
    this.setupFirstInteractionHandlers();
  }

  /**
   * Установка обработчиков для первого взаимодействия (iOS требует)
   */
  private setupFirstInteractionHandlers() {
    const handleFirstInteraction = () => {
      if (this.firstInteraction) {
        console.log('🎵 Первый клик/тап обнаружен!');
        this.tryStartMusicOnFirstInteraction();

        // Удаляем обработчики после первого вызова
        document.removeEventListener('click', handleFirstInteraction);
        document.removeEventListener('touchstart', handleFirstInteraction);
      }
    };

    document.addEventListener('click', handleFirstInteraction, { passive: true });
    document.addEventListener('touchstart', handleFirstInteraction, { passive: true });
  }

  /**
   * Установить состояние звука (включен/выключен)
   */
  setSoundEnabled(enabled: boolean) {
    // Защита от undefined/null - всегда используем boolean
    const normalizedEnabled = enabled !== false;

    // Логируем только если значение действительно изменилось
    if (this.soundEnabled !== normalizedEnabled) {
      console.log('🔊 Sound enabled changed:', this.soundEnabled, '->', normalizedEnabled);
    }

    this.soundEnabled = normalizedEnabled;

    // Если включаем звуки, убеждаемся что они инициализированы
    if (normalizedEnabled && !this.initialized) {
      console.log('🔊 Звуки включены но не инициализированы - инициализирую...');
      this.init();
    }
  }

  /**
   * Попытка запустить музыку при первом взаимодействии
   * iOS Safari требует явного взаимодействия пользователя для разблокировки аудио
   */
  private async tryStartMusicOnFirstInteraction() {
    if (this.firstInteraction) {
      this.firstInteraction = false;
      console.log('🎵 iOS: Первое взаимодействие - разблокируем аудио...');

      // 1. СИНХРОННО разблокируем audio context
      await this.unlockAudioContext();

      // 2. Инициализируем музыку если еще не инициализирована
      if (!this.backgroundMusic && this.musicEnabled) {
        this.initBackgroundMusic(this.currentTrack);
      }

      // 3. Запускаем музыку если она должна играть
      if (this.musicEnabled && this.backgroundMusic && this.backgroundMusic.paused) {
        console.log('🎵 Запускаем музыку после разблокировки...');
        this.playBackgroundMusic();
      }

      console.log('✅ Аудио система полностью разблокирована и готова');
    }
  }

  /**
   * Разблокировка аудио контекста для iOS Safari
   * Создаем и проигрываем тихий звук чтобы разблокировать audio
   * Также пытаемся проиграть один звук из каждого пула
   * ПУБЛИЧНЫЙ метод - вызывается из App.tsx при первом взаимодействии
   * @returns true если разблокировка успешна
   */
  async unlockAudioContext(): Promise<boolean> {
    // Если уже разблокирован - сразу возвращаем true
    if (this.audioContextUnlocked) {
      console.log('🔓 AudioContext уже разблокирован');
      return true;
    }

    try {
      // 1. Создаем временный silent audio для разблокировки основного контекста
      const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
      silentAudio.volume = 0.01;

      await silentAudio.play();
      console.log('🔓 iOS Audio Context разблокирован');
      silentAudio.pause();

      // 2. Разблокируем ВСЕ аудио объекты из пулов ПОСЛЕДОВАТЕЛЬНО
      const unlockPromises: Promise<void>[] = [];

      this.soundPools.forEach((pool, soundPath) => {
        if (pool.length > 0) {
          // Разблокируем первый аудио объект из каждого пула
          const audio = pool[0];
          const originalVolume = audio.volume;
          audio.volume = 0.001; // Почти беззвучно

          const unlockPromise = audio.play()
            .then(() => {
              audio.pause();
              audio.currentTime = 0;
              audio.volume = originalVolume;
              console.log(`🔓 Разблокирован звук: ${soundPath}`);
            })
            .catch((err) => {
              console.warn(`Не удалось разблокировать ${soundPath}:`, err);
            });

          unlockPromises.push(unlockPromise);
        }
      });

      // Ждем разблокировки всех звуков
      await Promise.all(unlockPromises);

      // Разблокируем backgroundMusic если он существует
      if (this.backgroundMusic) {
        console.log('🎵 Разблокируем backgroundMusic...');
        const bgVolume = this.backgroundMusic.volume;
        this.backgroundMusic.volume = 0.001;
        try {
          await this.backgroundMusic.play();
          this.backgroundMusic.pause();
          this.backgroundMusic.currentTime = 0;
          this.backgroundMusic.volume = bgVolume;
          console.log('🎵 backgroundMusic разблокирован');
        } catch (e) {
          this.backgroundMusic.volume = bgVolume;
          console.warn('🎵 Не удалось разблокировать backgroundMusic:', e);
        }
      }

      // Устанавливаем флаг успешной разблокировки
      this.audioContextUnlocked = true;
      console.log('✅ Все звуковые пулы разблокированы, audioContextUnlocked = true');

      return true;

    } catch (e) {
      console.warn('❌ Ошибка при разблокировке audio:', e);
      return false;
    }
  }

  /**
   * Проиграть звук используя следующий доступный объект из пула
   * @param soundPath - путь к звуковому файлу
   * @param volume - громкость от 0 до 1 (по умолчанию 1)
   */
  play(soundPath: string, volume: number = 1) {
    if (!this.soundEnabled) return;

    // Инициализируем при первом использовании
    if (!this.initialized) {
      this.init();
    }

    // При каждом звуке пытаемся запустить музыку и разблокировать аудио (для iOS)
    this.tryStartMusicOnFirstInteraction();

    const pool = this.soundPools.get(soundPath);
    if (!pool || pool.length === 0) {
      console.warn(`Sound pool not found: ${soundPath}`);
      return;
    }

    // Получаем текущий индекс и аудио-объект
    let currentIndex = this.poolIndices.get(soundPath) || 0;
    const audio = pool[currentIndex];

    // Переходим к следующему объекту в пуле (круговая ротация)
    currentIndex = (currentIndex + 1) % pool.length;
    this.poolIndices.set(soundPath, currentIndex);

    try {
      // Останавливаем текущее воспроизведение если идет
      if (!audio.paused) {
        audio.pause();
      }

      // Сбрасываем на начало
      audio.currentTime = 0;

      // Устанавливаем громкость
      audio.volume = Math.max(0, Math.min(1, volume));

      // Проигрываем
      const playPromise = audio.play();

      if (playPromise !== undefined) {
        playPromise.catch(async (err) => {
          // Автовоспроизведение заблокировано - пробуем разблокировать и переинициализировать
          if (err.name === 'NotAllowedError') {
            console.log('🔒 Autoplay blocked, unlocking audio context...');
            // Разблокируем аудио контекст (ждем завершения)
            await this.unlockAudioContext();
            // Пытаемся проиграть снова
            audio.currentTime = 0;
            audio.play().catch(e => console.warn('Retry failed:', e));
          } else {
            console.warn('Error playing sound:', err);
          }
        });
      }
    } catch (e) {
      console.warn('Error playing sound:', e);
    }
  }

  /**
   * Проиграть звук клика кнопки
   */
  playButtonClick() {
    this.play('ui/ui_button_click.mp3');
  }

  /**
   * Проиграть звук переключения вкладки
   */
  playTabSwitch() {
    this.play('ui/ui_tab_switch.mp3');
  }

  /**
   * Проиграть звук открытия модального окна
   */
  playModalOpen() {
    this.play('ui/ui_modal_open.mp3');
  }

  /**
   * Проиграть звук закрытия модального окна
   */
  playModalClose() {
    this.play('ui/ui_button_click.mp3');
  }

  /**
   * Подготовить звуки игры к воспроизведению (для iOS)
   * Просто делаем load() для предзагрузки без воспроизведения
   * Это безопасно и не создает конфликтов со звуками
   */
  prepareGameSounds() {
    const soundsToPreload = ['games/Winner_game.mp3', 'games/Loss_game.mp3', 'games/Game_start.mp3'];

    soundsToPreload.forEach(soundPath => {
      const pool = this.soundPools.get(soundPath);
      if (pool && pool.length > 0) {
        // Просто вызываем load() для предзагрузки
        pool.forEach(audio => {
          audio.load();
        });
      }
    });
    console.log('🎮 Звуки игры предзагружены');
  }

  /**
   * Проиграть звук начала игры
   * Примечание: preTouchResultSounds() убран отсюда т.к. создавал проблемы на iOS
   * (звуки накладывались друг на друга). Разблокировка audio context
   * происходит при первом взаимодействии в unlockAudioContext()
   */
  playGameStart() {
    this.play('games/Game_start.mp3');
  }

  /**
   * Проиграть звук победы в игре
   */
  playGameWin() {
    this.play('games/Winner_game.mp3');
  }

  /**
   * Проиграть звук проигрыша в игре
   */
  playGameLoss() {
    this.play('games/Loss_game.mp3');
  }

  /**
   * Проиграть звук нажатия на кнопку сбора
   */
  playClaimClick() {
    this.play('ui/claim_click.mp3');
  }

  /**
   * Проиграть звук успешного сбора (на 50% тише)
   */
  playClaimSuccess() {
    this.play('ui/claim_success.mp3', 0.5);
  }

  // 🎟️ Праздничный звук активации промокода Lunar Start (~5с)
  playPromoCelebrate() {
    this.play('ui/promo_celebrate.mp3', 0.7);
  }

  // 🎯 Праздничный звук создания Лунного Марафона (~4с, мягкий)
  playMarathonCelebrate() {
    this.play('ui/marathon_celebrate.mp3', 0.7);
  }

  /**
   * ANDROID FIX: Прямая инициализация и запуск музыки в одном синхронном вызове
   * Должен вызываться ПЕРВЫМ в контексте user gesture!
   */
  initBackgroundMusicDirect(trackKey: string = 'lunar_landing') {
    const track = this.MUSIC_TRACKS[trackKey as keyof typeof this.MUSIC_TRACKS];
    if (!track) {
      console.warn(`Track not found: ${trackKey}`);
      return;
    }

    // Если уже играет - не трогаем
    if (this.backgroundMusic && !this.backgroundMusic.paused) {
      console.log('🎵 [Android] Музыка уже играет');
      return;
    }

    // Останавливаем старую если есть
    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
    }

    console.log(`🎵 [Android] Создаём и запускаем музыку СРАЗУ: ${track.name}`);

    // Создаём Audio и СРАЗУ play() - всё синхронно в user gesture!
    this.backgroundMusic = new Audio(`/assets/music/${track.file}`);
    this.backgroundMusic.loop = true;
    this.backgroundMusic.volume = 0.3;
    this.currentTrack = trackKey;
    this.musicEnabled = true;

    // КРИТИЧНО: play() сразу после создания Audio, в том же call stack!
    const playPromise = this.backgroundMusic.play();
    if (playPromise) {
      playPromise
        .then(() => console.log('🎵 [Android] Музыка запущена успешно!'))
        .catch(err => console.warn('🎵 [Android] Play failed:', err.name, err.message));
    }
  }

  /**
   * Инициализация фоновой музыки (обычный метод)
   */
  initBackgroundMusic(trackKey: string = 'lunar_landing') {
    const track = this.MUSIC_TRACKS[trackKey as keyof typeof this.MUSIC_TRACKS];
    if (!track) {
      console.warn(`Track not found: ${trackKey}`);
      return;
    }

    // Не перезапускаем если уже играет тот же трек
    if (this.backgroundMusic && this.currentTrack === trackKey) {
      console.log(`🎵 Track already playing: ${track.name}, skipping reinit`);
      if (this.musicEnabled && this.backgroundMusic.paused) {
        this.playBackgroundMusic();
      }
      return;
    }

    // Останавливаем текущую музыку только если это другой трек
    if (this.backgroundMusic) {
      this.backgroundMusic.pause();
      this.backgroundMusic = null;
    }

    // Создаем новый аудио-объект
    this.backgroundMusic = new Audio(`/assets/music/${track.file}`);
    this.backgroundMusic.loop = true;
    this.backgroundMusic.volume = 0.3;
    this.backgroundMusic.preload = 'auto';

    this.currentTrack = trackKey;
    console.log(`🎵 Background music initialized: ${track.name}`);

    // Автоматически запускаем если музыка включена
    if (this.musicEnabled) {
      this.playBackgroundMusic();
    }
  }

  /**
   * Запустить/продолжить фоновую музыку
   * При NotAllowedError - разблокирует и пробует снова
   */
  playBackgroundMusic() {
    if (!this.backgroundMusic || !this.musicEnabled) return;

    console.log('🎵 Попытка воспроизведения музыки...');

    const playPromise = this.backgroundMusic.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('🎵 Музыка успешно запущена!');
        })
        .catch(async (err) => {
          if (err.name === 'NotAllowedError') {
            console.log('🎵 Autoplay blocked, разблокируем и пробуем снова...');
            // Разблокируем аудио контекст
            await this.unlockAudioContext();
            // Пробуем снова
            if (this.backgroundMusic && this.musicEnabled) {
              this.backgroundMusic.play()
                .then(() => console.log('🎵 Музыка запущена после разблокировки!'))
                .catch(e => console.warn('🎵 Retry failed:', e));
            }
          } else {
            console.warn('🎵 Ошибка воспроизведения музыки:', err);
          }
        });
    }
  }

  /**
   * Пауза фоновой музыки
   */
  pauseBackgroundMusic() {
    if (this.backgroundMusic && !this.backgroundMusic.paused) {
      this.backgroundMusic.pause();
    }
  }

  /**
   * Установить состояние музыки (включена/выключена)
   */
  setMusicEnabled(enabled: boolean) {
    // Защита от undefined/null - всегда используем boolean
    const normalizedEnabled = enabled !== false;

    // Логируем только если значение действительно изменилось
    if (this.musicEnabled !== normalizedEnabled) {
      console.log('🎵 Music enabled changed:', this.musicEnabled, '->', normalizedEnabled);
    }

    this.musicEnabled = normalizedEnabled;

    if (normalizedEnabled) {
      this.playBackgroundMusic();
    } else {
      this.pauseBackgroundMusic();
    }
  }

  /**
   * Переключить трек
   */
  switchBackgroundMusic(trackKey: string) {
    const isPlaying = this.backgroundMusic && !this.backgroundMusic.paused;
    this.initBackgroundMusic(trackKey);

    // Если музыка играла, запускаем новый трек автоматически
    if (isPlaying && this.musicEnabled) {
      this.playBackgroundMusic();
    }
  }

  /**
   * Установить громкость фоновой музыки (0.0 - 1.0)
   */
  setBackgroundMusicVolume(volume: number) {
    if (this.backgroundMusic) {
      this.backgroundMusic.volume = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Получить список доступных треков
   */
  getAvailableTracks() {
    return Object.entries(this.MUSIC_TRACKS).map(([key, track]) => ({
      key,
      name: track.name
    }));
  }

  /**
   * Получить текущий трек
   */
  getCurrentTrack() {
    return this.currentTrack;
  }
}

export default SoundManager.getInstance();
